import { CustomNavBar } from 'components/facets/CustomNavBar/CustomNavBar'
import { useUser } from 'components/providers/UserProvider'
import {
  createUserSubEntity,
  deleteUserSubEntity,
  editUserAttribute,
  editUserSubEntity,
} from 'lib/api'
import { useAppContext } from 'lib/hooks'
import { getUniqueNewName } from 'lib/utils'
import { useSnackbar } from 'notistack'
import qs from 'query-string'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation } from 'react-router-dom'
import useLocalStorage from 'react-use-localstorage'
import { DEFAULT_PRICING_STRATEGY_IDS, hasStartedPricing, PriceStategyTarget } from 'shared'
import { useHandleSettingsChange } from '../StockPricingPresenter'
import { BulkpriceEditor } from './BulkpriceEditor'
import { CompetitorPresenter } from './CompetitorPresenter'
import { CreateCompetitorDialog } from './CreateFlow/CreateCompetitorPresenter/CreateCompetitorDialog'
import { CreateStrategyDialog } from './CreateFlow/CreateStrategyPresenter/CreateStrategyDialog'
import './price-settings-presenter.css'
import { PricingBotPresenter } from './PricingBotPresenter/PricingBotPresenter'
import { PricingSettingsWelcome } from './PricingSettingsWelcome'
import { PricingStrategiesPresenter } from './PricingStrategiesPresenter'

type PricingSettingsTab =
  | 'minimum-prices'
  | 'competitors'
  | 'sell-strategies'
  | 'buy-strategies'
  | 'pricing-bot'

const ALL_TAB_KEYS: PricingSettingsTab[] = [
  'minimum-prices',
  'competitors',
  'sell-strategies',
  'buy-strategies',
  'pricing-bot',
]

/**
 * Highlights a tab the first time it transitions from disabled to enabled
 * during a session (the gating feature just got unlocked), once ever — the
 * "seen" flag is persisted in localStorage. Returns the highlight state and a
 * clearer to call once the user has acknowledged it.
 */
function useTabUnlockHighlight<T>(
  user: T | null | undefined,
  isDisabled: (u: T) => boolean,
  storageKey: string
): [boolean, () => void] {
  const [seen, setSeen] = useLocalStorage(storageKey, 'false')
  const [highlight, setHighlight] = useState(false)
  const prevDisabledRef = useRef<boolean | null>(null)

  useEffect(() => {
    if (!user) return
    const disabled = isDisabled(user)
    const prev = prevDisabledRef.current
    prevDisabledRef.current = disabled
    // First run only records the baseline — never highlight on mount.
    if (prev === null) return
    if (prev && !disabled && seen !== 'true') {
      setHighlight(true)
      setSeen('true')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, seen])

  const clear = useCallback(() => setHighlight(false), [])
  return [highlight, clear]
}

export const PricingSettings = (): JSX.Element => {
  const { t } = useTranslation()
  const { user, activeGame } = useUser()
  const { isMobile } = useAppContext()
  const { enqueueSnackbar } = useSnackbar()
  const [createStrategyDialogOpen, setCreateStrategyDialogOpen] = useState(false)
  const [createCompetitorDialogOpen, setCreateCompetitorDialogOpen] = useState(false)
  const [createStrategyTarget, setCreateStrategyTarget] = useState<PriceStategyTarget | null>(null)

  const handleChange = useHandleSettingsChange()

  const { search } = useLocation()
  const query = qs.parse(search)
  const initialTab = ALL_TAB_KEYS.includes(query.tab as PricingSettingsTab)
    ? (query.tab as PricingSettingsTab)
    : null
  const [activeTab, setActiveTab] = useState<PricingSettingsTab | null>(initialTab)

  // The tabs are an onboarding funnel: competitors unlock the strategy tabs, a sell
  // strategy unlocks the pricing bot. Owning a pricing plan already unlocks the bot too,
  // because a user who runs one is past that step however they got there — their
  // strategies may still carry a seeded id, or a target backfilled as sellPrice.
  // The strategy tabs stay open once the user has done any pricing work, even if they
  // later delete it all: the flag remembers it.
  const strategiesLocked = (u: NonNullable<typeof user>): boolean =>
    u.enabledNotifications?.displayFirstCompetitorPrompt !== false && !hasStartedPricing(u)
  const canUsePricingBot = (u: NonNullable<typeof user>): boolean =>
    (u.pricingPlans ?? []).length > 0 ||
    (u.pricingStrategies ?? []).some(
      (s) =>
        s.target !== PriceStategyTarget.buyPrice && !DEFAULT_PRICING_STRATEGY_IDS.includes(s._id)
    )

  // Highlight each tab the first time it unlocks.
  const [highlightStrategiesUnlock, clearStrategiesHighlight] = useTabUnlockHighlight(
    user,
    strategiesLocked,
    'pricingSettingsStrategiesUnlockSeen'
  )
  const [highlightBotUnlock, clearBotHighlight] = useTabUnlockHighlight(
    user,
    (u) => !canUsePricingBot(u),
    'pricingSettingsPricingBotUnlockSeen'
  )

  const rememberingPricingStarted = useRef(false)

  useEffect(() => {
    if (!user || rememberingPricingStarted.current) return
    if (user.enabledNotifications?.displayFirstCompetitorPrompt === false) return
    if (!hasStartedPricing(user)) return
    rememberingPricingStarted.current = true
    handleChange(() =>
      editUserAttribute(
        { ...user.enabledNotifications, displayFirstCompetitorPrompt: false },
        'enabledNotifications'
      )
    ).then((saved) => {
      if (!saved) rememberingPricingStarted.current = false
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user])

  if (!user) return <></>

  const bulkSettingsForActiveGame = user.bulkSettings.filter(
    (bulkSettings) => bulkSettings.gameId === activeGame.idGame
  )

  const strategiesDisabled = strategiesLocked(user)
  const pricingBotDisabled = !canUsePricingBot(user)

  const tabIsDisabled = (key: PricingSettingsTab): boolean => {
    if (key === 'sell-strategies' || key === 'buy-strategies') return strategiesDisabled
    if (key === 'pricing-bot') return pricingBotDisabled
    return false
  }
  const effectiveActiveTab: PricingSettingsTab | null =
    activeTab && tabIsDisabled(activeTab) ? 'minimum-prices' : activeTab

  // Numbered by position, so the count stays right whether or not the taxation
  // flag adds the buy-strategies tab.
  const tabs = [
    { key: 'minimum-prices', label: t('bulk.title'), dataTestid: 'tab-minimum-prices' },
    {
      key: 'competitors',
      label: t('competitor.yourCompetitors'),
      dataTestid: 'tab-competitors',
    },
    {
      key: 'sell-strategies',
      label: t('strategy.yourStrategies'),
      dataTestid: 'tab-sell-strategies',
      disabled: strategiesDisabled,
      disabledTooltip: strategiesDisabled
        ? t('pricingSettings.disabledNeedCompetitors')
        : undefined,
      highlight: highlightStrategiesUnlock,
    },
    ...(user.featureFlags?.taxation
      ? [
          {
            key: 'buy-strategies',
            label: t('strategy.yourBuyStrategies'),
            dataTestid: 'tab-buy-strategies',
            disabled: strategiesDisabled,
            disabledTooltip: strategiesDisabled
              ? t('pricingSettings.disabledNeedCompetitors')
              : undefined,
            highlight: highlightStrategiesUnlock,
          },
        ]
      : []),
    {
      key: 'pricing-bot',
      label: t('priceUpdates.pricingBot'),
      dataTestid: 'tab-pricing-bot',
      disabled: pricingBotDisabled,
      disabledTooltip: pricingBotDisabled
        ? t('pricingSettings.disabledNeedSellStrategies')
        : undefined,
      highlight: highlightBotUnlock,
    },
  ].map((tab, index) => ({ ...tab, label: `${index + 1}. ${tab.label}` }))

  const renderContent = (): JSX.Element => {
    if (effectiveActiveTab === null) {
      return <PricingSettingsWelcome />
    }
    switch (effectiveActiveTab) {
      case 'competitors':
        return (
          <CompetitorPresenter
            disabled={user.subscription.featuresDisabled.includes(
              'stockPricing.competitionPricing'
            )}
            competitors={user?.competitors}
            onChange={async (competitor) => {
              handleChange(() => editUserSubEntity(competitor, 'competitors'))
            }}
            onCreate={async () => setCreateCompetitorDialogOpen(true)}
            onDelete={async (id) => {
              handleChange(() => deleteUserSubEntity(id, 'competitors'))
            }}
            onClone={async (competitor) => {
              handleChange(() =>
                createUserSubEntity(
                  {
                    ...competitor,
                    name: getUniqueNewName(competitor.name, user.competitors),
                  },
                  'competitors'
                )
              )
            }}
          />
        )
      case 'sell-strategies':
      case 'buy-strategies':
        return (
          <PricingStrategiesPresenter
            target={
              effectiveActiveTab === 'buy-strategies'
                ? PriceStategyTarget.buyPrice
                : PriceStategyTarget.sellPrice
            }
            competitionDisabled={user.subscription.featuresDisabled.includes(
              'stockPricing.competitionPricing'
            )}
            competitors={user?.competitors}
            pricingStrategies={user?.pricingStrategies}
            onDelete={async (id) => {
              handleChange(() => deleteUserSubEntity(id, 'pricingStrategies'))
            }}
            onCreate={(target) => {
              setCreateStrategyTarget(target)
              setCreateStrategyDialogOpen(true)
            }}
            onChange={async (pricingStrategy) => {
              handleChange(() => editUserSubEntity(pricingStrategy, 'pricingStrategies'))
            }}
            onClone={async (pricingStrategy) => {
              await handleChange(() =>
                createUserSubEntity(
                  {
                    ...pricingStrategy,
                    name: getUniqueNewName(pricingStrategy.name, user.pricingStrategies),
                  },
                  'pricingStrategies'
                )
              )
              if (!user.canUsePricingBot) {
                await handleChange(() => editUserAttribute(true, 'canUsePricingBot'))
              }
            }}
            onConvertTo={
              user.featureFlags?.taxation
                ? async (pricingStrategy) => {
                    // handleChange swallows the error, so gate on its result:
                    // a failed conversion must not report success.
                    const ok = await handleChange(() =>
                      editUserSubEntity(pricingStrategy, 'pricingStrategies')
                    )
                    if (!ok) return
                    enqueueSnackbar(
                      t(
                        pricingStrategy.target === PriceStategyTarget.buyPrice
                          ? 'stockPricing.convertedToBuyPrice'
                          : 'stockPricing.convertedToSellPrice',
                        { name: pricingStrategy.name }
                      ),
                      { variant: 'success' }
                    )
                  }
                : undefined
            }
            bulkSettings={bulkSettingsForActiveGame}
          />
        )
      case 'pricing-bot':
        return <PricingBotPresenter handleChange={handleChange} />
      default:
        return <BulkpriceEditor />
    }
  }

  return (
    <>
      <div className={`w-100 mt-3 ${isMobile ? '' : 'ms-3'}`}>
        <CustomNavBar
          tabs={tabs}
          activeTab={effectiveActiveTab ?? ''}
          onTabChange={(key) => {
            if (key === 'sell-strategies' || key === 'buy-strategies') clearStrategiesHighlight()
            if (key === 'pricing-bot') clearBotHighlight()
            setActiveTab(key as PricingSettingsTab)
          }}
        />
        <div className="tab-page-content">{renderContent()}</div>
      </div>
      <CreateStrategyDialog
        onClose={() => setCreateStrategyDialogOpen(false)}
        open={createStrategyDialogOpen}
        target={createStrategyTarget!}
      />
      <CreateCompetitorDialog
        onClose={() => setCreateCompetitorDialogOpen(false)}
        open={createCompetitorDialogOpen}
      />
    </>
  )
}
